import { useStoreConnect } from '../../../common/hooks/useStoreConnect'
import { inputActions, outputActions } from '../../../api/actions/actionsLists'
import { sendMessage } from '../../../common/message/sendMessage'
import { getStore } from '@wellesley/store'
import { callHostApiAction } from '../../api'
import { MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { listenMessage } from '../../../common/message/listenMessage'
import { hostStoreConnect, remoteReady } from '../../../api/actions'
import { cloneDeep } from 'lodash/fp'

const allowedStoreNames = [
  'ws',
  'user',
  'auth',
  'ui',
  'profileSettings',
  'platformSettings',
  'router',
  'notifications',
  'instanceInfo',
  'group',
  'initialState',
]
export const prepareState = () => {
  const state = cloneDeep(getStore().getState())
  const filteredEntries = Object.entries(state).filter(([key]) =>
    allowedStoreNames.includes(key),
  )
  const filteredState = Object.fromEntries(filteredEntries)

  if (filteredState.auth) {
    filteredState.auth = { ...filteredState.auth, token: null }
  }

  return filteredState
}

export const useHostStoreConnect = (
  iframeRef: MutableRefObject<HTMLIFrameElement | null>,
  isActive: boolean,
  name?: string,
) => {
  const disconnectRef = useRef<() => void>(() => {})
  const connect = useStoreConnect({
    sourceWindow: iframeRef.current?.contentWindow ?? null,
    sendWhitelist: outputActions,
    receiveWhitelist: inputActions,
    onMessage: (action) =>
      void (callHostApiAction(action) || getStore().dispatch(action)),
    onAction: (action) => void callHostApiAction(action),
    name,
  })

  const sendStoreState = useCallback(() => {
    const action = hostStoreConnect(prepareState())
    sendMessage({
      action,
      windowElement: iframeRef.current?.contentWindow ?? null,
      whitelist: [action],
    })
  }, [iframeRef, isActive])

  const init = useCallback(() => {
    if (!isActive) return

    const disconnect = connect(iframeRef.current?.contentWindow ?? null)
    disconnectRef.current = disconnect ?? (() => {})
    sendStoreState()
  }, [connect, iframeRef, sendStoreState, isActive])

  useEffect(() => {
    if (isActive) init()
    else disconnectRef.current()
  }, [isActive])

  useEffect(() => {
    const unsubscribe = listenMessage({
      sourceWindow: iframeRef.current?.contentWindow ?? null,
      onMessage: (action) => {
        action.type === remoteReady.type && init()
      },
      whitelist: [remoteReady],
    })

    return () => unsubscribe()
  }, [init])
}
